import { html, useEffect, useState } from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';
import { myCircles } from '/circlechoice.js';
import { enqueue, flush } from '/queue.js';
import { AddToCircle } from '/members.js';
import { Icon } from '/icons.js';
import { CLOCKS, recall, remember } from '/clocks.js';
import { Combo, Stepper } from '/stepper.js';
import { FORMATS, nameOf } from '/formats.js';

// First to N, or best of 2N-1. Best of moves in twos because an even "best of"
// can be drawn, and a drawn frame count is not a result anybody wants.
const MODES = [
  { id: 'first', label: 'First to', say: (n) => `First to ${n} frames takes it.` },
  { id: 'best', label: 'Best of',
    say: (n) => `Best of ${n} — first to ${Math.ceil(n / 2)} takes it.` },
];

/**
 * Starting a match — one screen that fills itself in.
 *
 * It used to be five: circle, opponent, format, race, ready, each behind a
 * CONTINUE that stayed dead until you answered it. Five taps of chrome before
 * the one tap you came for, no way to see what you had already chosen, and no
 * way to change it without walking back through the others.
 *
 * Everything is on one screen now, and a section appears as soon as the thing
 * it depends on has an answer — pick a circle and it asks who you are playing,
 * pick them and it asks about the game. Nothing that has appeared disappears,
 * so a wrong tap is fixed by tapping the right one.
 *
 * The score for a match already played still hangs off the end rather than
 * sitting in the middle: it is the rarer path, and having both in view at once
 * was the muddle that made this a wall of controls in the first place.
 */
export function Record({ onDone, onStarted }) {
  const [me, setMe] = useState(null);
  const [circles, setCircles] = useState([]);
  const [accounts, setAccounts] = useState([]);   // everyone with a login
  const [circleId, setCircleId] = useState('');
  const [newCircle, setNewCircle] = useState('');
  const [opponentId, setOpponentId] = useState('');
  const [format, setFormat] = useState('blackball');
  // Who breaks the first frame — GitHub issue #60. `null` means "whoever
  // opens the match", which is what the break was before there was a choice
  // and what the server does with an absent field. Deliberately NOT
  // defaulted to `me` on load: `me` arrives asynchronously, and seeding this
  // from it would make the control read as a decision somebody made.
  const [breakerId, setBreakerId] = useState(null);
  const [mode, setMode] = useState('first');
  const [raceTo, setRaceTo] = useState(5);
  const [bestOf, setBestOf] = useState(9);
  const [shotOn, setShotOn] = useState(() => recall('cueforge.clock.shot.on', 0) === 1);
  const [shotSecs, setShotSecs] = useState(() => recall(CLOCKS[0].key, 30) || 30);
  const [matchOn, setMatchOn] = useState(() => recall('cueforge.clock.match.on', 0) === 1);
  const [matchMins, setMatchMins] = useState(() => recall(CLOCKS[1].key, 15) || 15);
  const [mine, setMine] = useState('');
  const [theirs, setTheirs] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [auth, mine_, all] = await Promise.all(
      [get('/api/auth/state'), myCircles(), get('/api/people')]);
    setMe(auth.user_id);
    setCircles(mine_.circles);
    setAccounts(all);
    if (!circleId && mine_.circles.length) setCircleId(mine_.circles[0].id);
    return { circles: mine_.circles, accounts: all };
  };

  // A failure here left every list empty for good, with nothing said.
  useEffect(() => {
    reload().catch((e) => setError(e.detail || e.message));
  }, []);

  useEffect(() => {
    const onOnline = () => flush();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const guard = (fn) => async () => {
    setBusy(true); setError(''); setResult('');
    try { await fn(); }
    catch (e) { setError(e.detail || e.message); }
    finally { setBusy(false); }
  };

  // Offered whether or not you already have a circle. Gating it on having none
  // meant that once you had one there was no way to make a second — so the
  // circle selector, which only appears with two, could never be reached.
  const createCircle = guard(async () => {
    // Not until we know who you are. Creating a circle is two calls — make it,
    // then join it — and running them before /api/auth/state answered sent
    // user_id: null, so the circle was created with NOBODY in it. It then did
    // not appear in your own list, and the screen sat there offering to create
    // another. The button is disabled until `me` lands; this is the same rule
    // stated where it cannot be styled away.
    if (!me) return;
    const id = (await post('/api/circles',
      { name: newCircle || 'Matches' })).circle_id;
    await post(`/api/circles/${id}/members`, { user_id: me });
    setCircleId(id);
    setNewCircle('');
    await reload();
  });

  // Whoever was just added is who you are about to play — that is why you
  // added them.
  const added = async (id) => { setOpponentId(id); await reload(); };

  // Best of N is won by taking more than half of it, so the server only ever
  // hears one number: how many frames it takes to win.
  const target = mode === 'best' ? Math.ceil(bestOf / 2) : raceTo;
  const clocks = () => ({
    shot_seconds: shotOn ? Number(shotSecs) : 0,
    match_seconds: matchOn ? Number(matchMins) * 60 : 0,
  });

  const startLive = guard(async () => {
    const started = await post('/api/matches/start', {
      circle_id: circleId, opponent_id: opponentId, game_format: format,
      race_to: Number(target),
      ...clocks(),
      // Absent rather than null when nobody chose, so the server applies its
      // own default instead of being handed one this screen invented.
      ...(breakerId ? { breaker_id: breakerId } : {}),
      event_id: crypto.randomUUID(),
    });
    onStarted(started.match_id);
  });

  const submit = guard(async () => {
    const body = {
      circle_id: circleId, opponent_id: opponentId, game_format: format,
      race_to: Number(target), played_at: new Date().toISOString(),
      score: [Number(mine), Number(theirs)],
      event_id: crypto.randomUUID(),
    };
    if (!navigator.onLine) {
      enqueue(body);
      setResult('Saved on this device — it will sync when you have signal.');
    } else {
      const saved = await post('/api/matches', body);
      setResult(saved.status === 'provisional'
        ? 'Recorded — waiting for them to confirm.'
        : `Recorded — ${saved.status}.`);
    }
  });

  // Derived from the CHOSEN circle, never from a flat list across all of them:
  // the server refuses an opponent who is not in the circle being played in,
  // so anything wider offers matches that cannot be recorded.
  const circle = circles.find((c) => c.id === circleId);
  const people = (circle && circle.people) || [];
  const inCircle = new Set(people.map((p) => p.user_id));
  const addable = accounts.filter((a) => !inCircle.has(a.user_id)
                                         && a.user_id !== me);
  const chosen = people.find((p) => p.user_id === opponentId);
  const opponentKind = chosen && chosen.kind;

  // Switching circles must not leave an opponent from the previous one
  // selected — the form would look fine and the server would refuse it.
  useEffect(() => {
    if (!people.length) { if (opponentId) setOpponentId(''); return; }
    if (!chosen) setOpponentId(people[0].user_id);
  }, [circleId, people.length, opponentId]);

  // What is answered decides what is ASKED — not which screen you are on.
  // A section appears once the thing it needs has a value, and stays.
  const askOpponent = Boolean(circleId);
  const askGame = askOpponent && Boolean(opponentId);
  const ready = Boolean(circleId && opponentId && format && raceTo);

  const clockValue = { shot: shotSecs, match: matchMins };
  const setClock = {
    shot: (v) => { setShotSecs(v); remember(CLOCKS[0].key, v); },
    match: (v) => { setMatchMins(v); remember(CLOCKS[1].key, v); },
  };

  return html`
    <div class="matchbar">
      <button data-testid="record-back" onClick=${onDone} aria-label="Back">
        <${Icon} name="chevron-left" size=${20} />
      </button>
      <span data-testid="record-stage">START A MATCH</span>
      <span style="width:20px"></span>
    </div>

    <div class="screen">
      <div class="stack" data-testid="record-step-circle">
        <label>Whose circle</label>
        ${/* A list, not a row of buttons: it is the platform's own picker on a
              phone, it reads a long name without running off the edge, and it
              does not grow wider with every circle you join. Making one lives
              on the Circles tab, which is where you go to think about circles
              rather than about the match you are trying to start. */''}
        <${Combo} testid="record-circle" value=${circleId}
                  placeholder=${circles.length ? '' : 'No circles yet'}
                  options=${circles.map((c) => ({ id: c.id, label: c.name }))}
                  onChange=${(id) => { setCircleId(id); setOpponentId(''); }} />
        ${circles.length === 0 && html`
          <p class="sub" data-testid="record-no-circles">
            You are not in a circle yet — make one on the Circles tab.
          </p>`}
      </div>

      ${askOpponent && html`
        <div class="stack" data-testid="record-step-opponent">
          <label>Who are you playing</label>
          ${people.length > 0
            ? html`
              <${Combo} testid="record-opponent" value=${opponentId}
                        options=${people.map((p) => ({
                          id: p.user_id,
                          label: p.kind === 'ghost' ? `${p.name} (guest)` : p.name,
                        }))}
                        onChange=${setOpponentId} />`
            : html`<p class="sub">Nobody in this circle yet — add someone.</p>`}

          ${opponentKind === 'ghost' && html`
            <p class="pending" data-testid="record-guest-warning">
              A guest has no account, so this counts straight away and they will
              never see it. If they have signed up, add them from the list
              instead.
            </p>`}

          <${AddToCircle} ask=${false} circleId=${circleId} addable=${addable}
                          onAdded=${added} prefix="record" />
        </div>`}

      ${askGame && html`
        <div class="stack" data-testid="record-step-game">
          <label>Game</label>
          <div class="seg" data-testid="record-format">
            ${/* Killer is not offered here. It is several people round one
                 table with lives instead of frames, and this screen opens a
                 match between two — choosing it here would have recorded a
                 1-v-1 wearing the wrong name. It is opened from the tablet on
                 the wall, where the people playing it are standing. */
              FORMATS.filter((f) => !f.venueOnly).map((f) => html`
              <button key=${f.id} data-testid=${`format-${f.id}`}
                      aria-pressed=${format === f.id}
                      onClick=${() => setFormat(f.id)}>${f.label}</button>`)}
          </div>
        </div>

        <div class="stack" data-testid="record-step-raceto">
          <label>Length</label>
          <div class="seg" data-testid="record-mode">
            ${MODES.map((m) => html`
              <button key=${m.id} data-testid=${`mode-${m.id}`}
                      aria-pressed=${mode === m.id}
                      onClick=${() => setMode(m.id)}>${m.label}</button>`)}
          </div>
          ${mode === 'first'
            ? html`<${Stepper} testid="raceto" label="frames" value=${raceTo}
                               min=${1} max=${99} step=${1}
                               onChange=${setRaceTo} />`
            : html`<${Stepper} testid="bestof" label="frames" value=${bestOf}
                               min=${3} max=${99} step=${2}
                               onChange=${setBestOf} />`}
          <p class="sub" data-testid="record-length-says">
            ${MODES.find((m) => m.id === mode)
              .say(mode === 'best' ? bestOf : raceTo)}
          </p>
        </div>

        ${/* Who breaks first — issue #60. It ALTERNATES from there, so naming
              the first frame names them all, and the note says so: a picker
              that looked like it applied to every frame would be promising
              something the rules do not allow.

              Only once both players are known. Before that the two buttons
              would have nobody to name, and a control labelled "You" and
              "Loading…" is worse than one that has not appeared yet. */''}
        ${me && chosen && html`
          <div class="stack" data-testid="record-step-breaks">
            <label>Breaks first</label>
            <div class="seg" data-testid="record-breaker">
              ${[[me, 'You'], [chosen.user_id, chosen.name]]
                .map(([id, label]) => html`
                  <button key=${id} data-testid=${`breaker-${id}`}
                          aria-pressed=${(breakerId || me) === id}
                          onClick=${() => setBreakerId(id)}>${label}</button>`)}
            </div>
            <p class="sub" data-testid="record-breaks-says">
              ${`${(breakerId || me) === me
                   ? 'You break' : `${chosen.name} breaks`} the first frame, `
                + 'and it alternates after that.'}
            </p>
          </div>`}

        ${/* Off until switched on, and then a figure you step rather than a
              row of presets. A clock is a thing you set once for a league
              night, not a decision to re-take every match. */''}
        <div class="stack" data-testid="record-step-shot">
          <label>Shot clock</label>
          <div class="row pair">
            <button class=${shotOn ? '' : 'secondary'} data-testid="shot-toggle"
                    onClick=${() => { const v = !shotOn; setShotOn(v);
                                      remember('cueforge.clock.shot.on', v ? 1 : 0); }}>
              ${shotOn ? 'On' : 'Off'}
            </button>
            ${shotOn && html`
              <${Stepper} testid="shot" label="seconds" value=${shotSecs}
                          min=${15} max=${300} step=${15}
                          format=${(v) => `${v}s`}
                          onChange=${(v) => { setShotSecs(v);
                                              remember(CLOCKS[0].key, v); }} />`}
          </div>
        </div>

        <div class="stack" data-testid="record-step-match">
          <label>Match clock</label>
          <div class="row pair">
            <button class=${matchOn ? '' : 'secondary'} data-testid="match-toggle"
                    onClick=${() => { const v = !matchOn; setMatchOn(v);
                                      remember('cueforge.clock.match.on', v ? 1 : 0); }}>
              ${matchOn ? 'On' : 'Off'}
            </button>
            ${matchOn && html`
              <${Stepper} testid="match" label="minutes" value=${matchMins}
                          min=${5} max=${180} step=${5}
                          format=${(v) => `${v}m`}
                          onChange=${(v) => { setMatchMins(v);
                                              remember(CLOCKS[1].key, v); }} />`}
          </div>
        </div>`}

      ${ready && html`
        <div style="text-align:center">
          <h1 class="gold" data-testid="record-ready">
            ${`V ${((chosen && chosen.name) || '').toUpperCase()}`}
          </h1>
          <p class="sub mono" style="margin:.5rem auto 0"
             data-testid="record-summary">
            ${`${nameOf(format)}`
              + ` · ${mode === 'best' ? `BEST OF ${bestOf}` : `RACE TO ${raceTo}`}`
              + ` · ${(circle || {}).name || ''}`}
          </p>
        </div>

        <button class="felt" data-testid="record-start"
                disabled=${busy || !opponentId} onClick=${startLive}>
          <${Icon} name="circle-dot" size=${18} />
          <span>Start playing</span>
        </button>

        <div class="stack">
          <label>Or a match you have already played</label>
          <div class="row">
            <div class="stack">
              <label>You</label>
              <input data-testid="record-mine" type="number" inputmode="numeric"
                     value=${mine} onInput=${(e) => setMine(e.target.value)} />
            </div>
            <div class="stack">
              <label>Them</label>
              <input data-testid="record-theirs" type="number" inputmode="numeric"
                     value=${theirs} onInput=${(e) => setTheirs(e.target.value)} />
            </div>
          </div>
          <p class="sub" data-testid="record-approx" style="margin:.6rem 0 0">
            Without the frame order this is rated as one result, so it moves
            your rating a little differently from playing it out.
          </p>
          <button class="secondary" data-testid="record-submit"
                  disabled=${busy || !opponentId} onClick=${submit}>
            Save the score
          </button>
        </div>`}

      <button class="secondary" data-testid="record-done" onClick=${onDone}>
        Done
      </button>

      ${result && html`<p class="ok" data-testid="record-result">${result}</p>`}
      ${error && html`<p class="error" data-testid="error">${error}</p>`}
    </div>`;
}
